import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Spin, Table } from "antd";
import { LoadingOutlined, DownloadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { adminContext } from "../../Common/PageRoute";
import DailyPracticeStats from "../../Services/DailyPracticeStats";
import {
  PageLayout,
  PageHeader,
  SectionCard,
  TableToolbar,
  EmptyState,
  FilterCriteria,
} from "../../Common/Layout";
import {
  DailyPracticeRecord,
  PracticeFiltersState,
  PracticeMetricStats,
  SortState,
  PaginationState,
} from "./types";
import PracticeQuickDrawer from "./PracticeQuickDrawer";
import PracticeStatsRow from "./PracticeStatsRow";
import usePracticeColumns from "./usePracticeColumns";
import { exportPracticeReportsToCsv } from "./practiceUtils";

const DEFAULT_SORT: SortState = {
  sortCol: "practiceId",
  sortDir: "ascend",
};

const DEFAULT_PAGINATION: PaginationState = {
  currentPage: 1,
  pageSize: 15,
  totalRecords: 0,
};

export const PracticeReports: React.FC = () => {
  const { openNotification } = React.useContext(adminContext);
  const navigate = useNavigate();

  // Data state
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [dailyPracticeData, setDailyPracticeData] = useState<{
    items: DailyPracticeRecord[];
    totalRecords: number;
  }>({
    items: [],
    totalRecords: 0,
  });

  // Inspection drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] =
    useState<DailyPracticeRecord | null>(null);

  // Sorting & pagination state
  const [currentSort, setCurrentSort] = useState<SortState>(DEFAULT_SORT);
  const [pagination, setPagination] =
    useState<PaginationState>(DEFAULT_PAGINATION);

  // Filters state
  const [filters, setFilters] = useState<PracticeFiltersState>({
    search: "",
    fromDate: dayjs().startOf("month"),
    toDate: dayjs(),
  });

  const fetchPracticeStats = useCallback(
    async (
      page: number = pagination.currentPage,
      size: number = pagination.pageSize,
      sortCol: string = currentSort.sortCol,
      sortDir: "ascend" | "descend" = currentSort.sortDir,
    ) => {
      setLoading(true);
      try {
        const start = (page - 1) * size;
        const params: any = {
          start,
          length: size,
          sortCol,
          sortDir,
        };

        if (filters.search) params.search = filters.search.trim();
        if (filters.fromDate) {
          params.fromDate = filters.fromDate.startOf("day").toISOString();
        }
        if (filters.toDate) {
          params.toDate = filters.toDate.endOf("day").toISOString();
        }

        const res: any = await DailyPracticeStats.getDailyPracticeStats(params);
        const items: DailyPracticeRecord[] =
          res?.result?.items || res?.items || (Array.isArray(res) ? res : []);
        const totalRecords: number =
          res?.result?.totalRecords ?? res?.totalRecords ?? items.length;

        setDailyPracticeData({ items, totalRecords });
        setPagination((prev) => ({
          ...prev,
          currentPage: page,
          pageSize: size,
          totalRecords,
        }));
      } catch (error) {
        console.error("Failed to fetch daily practice statistics:", error);
        openNotification(
          "error",
          "Failed to load practice statistics. Please try again.",
        );
      } finally {
        setLoading(false);
      }
    },
    [
      currentSort.sortCol,
      currentSort.sortDir,
      filters,
      openNotification,
      pagination.currentPage,
      pagination.pageSize,
    ],
  );

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const params: any = {
        start: 0,
        length: dailyPracticeData.totalRecords || 100000,
        sortCol: currentSort.sortCol,
        sortDir: currentSort.sortDir,
      };
      if (filters.search) params.search = filters.search.trim();
      if (filters.fromDate) {
        params.fromDate = filters.fromDate.startOf("day").toISOString();
      }
      if (filters.toDate) {
        params.toDate = filters.toDate.endOf("day").toISOString();
      }

      const res: any = await DailyPracticeStats.getDailyPracticeStats(params);
      const items: DailyPracticeRecord[] =
        res?.result?.items || res?.items || (Array.isArray(res) ? res : []);

      const suffix =
        filters.fromDate && filters.toDate
          ? `_${filters.fromDate.format("YYYYMMDD")}-${filters.toDate.format("YYYYMMDD")}`
          : "";
      exportPracticeReportsToCsv(items, suffix);
    } catch (error) {
      console.error("Failed to export practice reports:", error);
      openNotification(
        "error",
        "Failed to export practice reports. Please try again.",
      );
    } finally {
      setExporting(false);
    }
  }, [
    currentSort.sortCol,
    currentSort.sortDir,
    dailyPracticeData.totalRecords,
    filters,
    openNotification,
  ]);

  useEffect(() => {
    fetchPracticeStats(
      1,
      pagination.pageSize,
      currentSort.sortCol,
      currentSort.sortDir,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.fromDate, filters.toDate, refreshKey]);

  const handleTableChange = (
    tablePagination: any,
    _filters: any,
    sorter: any,
  ) => {
    let sortCol = DEFAULT_SORT.sortCol;
    let sortDir: "ascend" | "descend" = "ascend";

    if (sorter?.order) {
      sortDir = sorter.order === "descend" ? "descend" : "ascend";
    }

    if (sorter?.field) {
      let field = sorter.field;
      if (Array.isArray(field)) field = field.join(",");
      if (typeof field === "string" && field.includes(",")) {
        sortCol = field.split(",")[1];
      } else {
        sortCol = field;
      }
    }

    const newPage = tablePagination.current || 1;
    const newPageSize = tablePagination.pageSize || 15;

    setCurrentSort({ sortCol, sortDir });
    fetchPracticeStats(newPage, newPageSize, sortCol, sortDir);
  };

  const handleViewQuickSummary = useCallback((record: DailyPracticeRecord) => {
    setSelectedRecord(record);
    setDrawerOpen(true);
  }, []);

  const handleManagePractice = useCallback(
    (record: DailyPracticeRecord) => {
      navigate(`/practices/${record.practiceId}`, {
        state: { practiceName: record.practiceName },
      });
    },
    [navigate],
  );

  const { columns, components, columnSelector } = usePracticeColumns({
    currentPage: pagination.currentPage,
    pageSize: pagination.pageSize,
    currentSort,
    onViewQuickSummary: handleViewQuickSummary,
    onManagePractice: handleManagePractice,
  });

  const filterCriteria: FilterCriteria[] = useMemo(
    () => [{ key: "dateRange", label: "Date Range", type: "dateRange" }],
    [],
  );

  const activeFilters = useMemo(
    () => ({
      dateRange:
        filters.fromDate && filters.toDate
          ? [filters.fromDate, filters.toDate]
          : undefined,
    }),
    [filters.fromDate, filters.toDate],
  );

  const handleFiltersChange = useCallback((newFilters: Record<string, any>) => {
    const dates = newFilters.dateRange || [null, null];
    setFilters((prev) => ({
      ...prev,
      fromDate: dates[0],
      toDate: dates[1],
    }));
  }, []);

  const dateRangeLabel = useMemo(() => {
    if (filters.fromDate && filters.toDate) {
      return `${filters.fromDate.format("DD/MM/YYYY")} - ${filters.toDate.format("DD/MM/YYYY")}`;
    }
    return "All Time";
  }, [filters.fromDate, filters.toDate]);

  return (
    <>
      <PageLayout maxWidth="100%">
        <PageHeader
          title="Practice Reports"
          subtitle="Multi-tenant financial telemetry, statutory HMRC filings, and communications usage"
        />

        {/* Top Aggregated Metric Cards */}
        <PracticeStatsRow
          items={dailyPracticeData.items}
          totalRecords={dailyPracticeData.totalRecords}
        />

        {/* Practice Analytics Table with Column Customization & Filters */}
        <SectionCard noPadding>
          <div style={{ padding: "6px 14px" }}>
            <TableToolbar
              onSearch={(val) =>
                setFilters((prev) => ({ ...prev, search: val }))
              }
              searchPlaceholder="Search practice name..."
              filterCriteria={filterCriteria}
              activeFilters={activeFilters}
              onFiltersChange={handleFiltersChange}
              onRefresh={() => setRefreshKey((k) => k + 1)}
              primaryActions={[
                {
                  key: "export",
                  label: "Export CSV",
                  icon: <DownloadOutlined />,
                  onClick: handleExport,
                  disabled: exporting || dailyPracticeData.totalRecords === 0,
                },
              ]}
              extra={columnSelector}
            />
          </div>

          <Table
            dataSource={dailyPracticeData.items}
            columns={columns}
            components={components}
            rowKey="practiceId"
            size="small"
            tableLayout="fixed"
            loading={{
              spinning: loading,
              indicator: <Spin indicator={<LoadingOutlined />} />,
            }}
            className="border-top-table"
            scroll={{
              x: "max-content",
              y: "calc(100vh - 420px)",
            }}
            locale={{
              emptyText: (
                <EmptyState
                  title="No practice reports found"
                  description="Try adjusting your search query or date filter"
                />
              ),
            }}
            pagination={{
              current: pagination.currentPage,
              pageSize: pagination.pageSize,
              total: dailyPracticeData.totalRecords,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} practices`,
              showSizeChanger: true,
              pageSizeOptions: ["15", "25", "50", "100"],
            }}
            onChange={handleTableChange}
          />
        </SectionCard>
      </PageLayout>

      {/* Slide-over Inspection Drawer */}
      <PracticeQuickDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedRecord(null);
        }}
        record={selectedRecord}
        dateRangeText={dateRangeLabel}
      />
    </>
  );
};

export default PracticeReports;
